import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { usePartiesByNation } from '@/hooks/usePartiesByNation'
import { useCanCreateParty } from '@/hooks/useCanCreateParty'
import { useMyPartyInNation } from '@/hooks/useMyPartyInNation'
import { useAuth } from '@/hooks/useAuth'
import { useLastCompletedElection } from '@/hooks/useLastCompletedElection'
import { useUpcomingElection } from '@/hooks/useUpcomingElection'
import { ElectionCountdown } from '@/components/election/ElectionCountdown'
import { AppLayout } from '@/components/layout/AppLayout'
import { NationRulesCard } from '@/components/nation/NationRulesCard'
import { NationPartiesList } from '@/components/nation/NationPartiesList'
import { NationRegionsList } from '@/components/nation/NationRegionsList'
import { JoinNationCTA } from '@/components/nation/JoinNationCTA'
import { NationPoliticalLandscape } from '@/components/nation/NationPoliticalLandscape'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Users, Settings, Vote } from 'lucide-react'
import { AdminOnly } from '@/components/AdminOnly'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'

export default function NationOverview() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { nation, isLoading: nationLoading, error: nationError } = useNation(id)
  const { world, isLoading: worldLoading } = useWorld(nation?.world_id)
  const { regions, isLoading: regionsLoading, error: regionsError } = useRegionsByNation(id)
  const { parties, isLoading: partiesLoading, error: partiesError } = usePartiesByNation(id)
  const { canCreate, isLoading: canCreateLoading } = useCanCreateParty(id)
  const { party: myParty, isLoading: myPartyLoading } = useMyPartyInNation(id)
  const { election: lastElection } = useLastCompletedElection(id)
  const { election: upcomingElection } = useUpcomingElection(id)

  const isLoading = nationLoading || worldLoading
  const error = nationError

  const activeParties = parties?.filter((p) => p.is_active) || []

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name })
    }
    return items
  }, [world, nation])

  // Calculate total seats across all regions
  const totalSeats = regions?.reduce((sum, r) => sum + r.seat_count, 0) || 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {nationError?.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'Nation not found.'
            : 'Failed to load nation. Please try again later.'}
        </div>
      )}

      {isLoading && (
        <div className="space-y-6">
          <div className="bg-muted h-8 w-48 animate-pulse rounded" />
          <div className="bg-muted h-5 w-64 animate-pulse rounded" />
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-32 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-muted h-4 w-40 animate-pulse rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-28 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-muted h-4 w-36 animate-pulse rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {!isLoading && !error && nation && (
        <>
          {/* Header Section */}
          <div className="mb-8">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{nation.name}</h1>
                <Badge variant="outline" className="text-sm">
                  {world?.name}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {lastElection && (
                  <Button variant="outline" asChild>
                    <Link to={`/nations/${nation.id}/elections/${lastElection.id}`}>
                      <Vote className="mr-2 h-4 w-4" />
                      Election Results
                    </Link>
                  </Button>
                )}
                <Button variant="outline" asChild>
                  <Link to={`/nations/${nation.id}/demographics`}>
                    <Users className="mr-2 h-4 w-4" />
                    Voter Demographics
                  </Link>
                </Button>
                <AdminOnly>
                  <Button variant="outline" asChild>
                    <Link to={`/nations/${nation.id}`}>
                      <Settings className="mr-2 h-4 w-4" />
                      Admin View
                    </Link>
                  </Button>
                </AdminOnly>
              </div>
            </div>
            <p className="text-muted-foreground">
              Explore the political landscape and join the democratic process
            </p>
          </div>

          {/* Quick Stats */}
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{activeParties.length}</div>
                <p className="text-muted-foreground text-sm">
                  Active Parties (max {nation.max_parties})
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{regions?.length || 0}</div>
                <p className="text-muted-foreground text-sm">Regions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{totalSeats}</div>
                <p className="text-muted-foreground text-sm">Parliament Seats</p>
              </CardContent>
            </Card>
          </div>

          {/* Election Countdown */}
          {world && (
            <div className="mb-8">
              <ElectionCountdown
                currentTick={world.current_tick}
                nextElectionTick={nation.next_election_tick}
                termLengthTicks={nation.term_length_ticks ?? 16}
                campaignStartTick={upcomingElection?.campaign_start_tick}
                electionStatus={upcomingElection?.status ?? (lastElection?.status === 'completed' ? 'completed' : null)}
                latestCompletedElectionId={lastElection?.id}
                nationId={nation.id}
              />
            </div>
          )}

          {/* Join CTA - Prominent placement for new players */}
          <JoinNationCTA
            nationId={nation.id}
            maxParties={nation.max_parties}
            activePartyCount={activeParties.length}
            myParty={myParty}
            canCreate={canCreate}
            isLoading={canCreateLoading || myPartyLoading}
            isLoggedIn={!!user}
          />

          {/* Political Landscape Map */}
          <div className="mb-8">
            <NationPoliticalLandscape
              parties={activeParties}
              myPartyId={myParty?.id}
              isLoading={partiesLoading}
            />
          </div>

          {/* Constitutional Rules */}
          <div className="mb-8">
            <h2 className="mb-4 text-xl font-semibold">Constitutional Framework</h2>
            <NationRulesCard nation={nation} />
          </div>

          {/* Parties Section */}
          <div className="mb-8 border-t pt-6">
            <NationPartiesList
              parties={activeParties}
              maxParties={nation.max_parties}
              myPartyId={myParty?.id}
              isLoading={partiesLoading}
              error={partiesError}
            />
          </div>

          {/* Regions Section */}
          <div className="border-t pt-6">
            <NationRegionsList
              regions={regions || []}
              isLoading={regionsLoading}
              error={regionsError}
            />
          </div>
        </>
      )}
    </AppLayout>
  )
}
